import { useParams, Link, useNavigate } from 'react-router-dom'
import { Play, Clock, CheckCircle, XCircle, Loader, ChevronRight, Shield, FileText, BarChart2, Search as SearchIcon, Zap, Globe } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { apiPost } from '../lib/auth'
import { ScoreGauge } from '../components/ScoreGauge'
import { KpiCard } from '../components/ui/KpiCard'
import { ContentHeader } from '../components/layout/ContentHeader'
import { EmptyState } from '../components/ui/EmptyState'
import { useState } from 'react'

interface Client {
  id: number
  name: string
  domain: string
  gsc_property: string | null
}

interface Audit {
  id: number
  status: 'pending' | 'running' | 'completed' | 'failed'
  score: number | null
  scoreTech: number | null
  scoreOnpage: number | null
  scorePerf: number | null
  scoreSerp: number | null
  pagesCount: number
  createdAt: string
  completedAt: string | null
  errorMsg: string | null
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function scoreColor(score: number): string {
  if (score >= 90) return '#10B981'
  if (score >= 70) return '#84CC16'
  if (score >= 50) return '#FBBF24'
  if (score >= 30) return '#F97316'
  return '#EF4444'
}

function statusBadge(status: Audit['status']) {
  const configs = {
    completed: { icon: CheckCircle, label: 'Termine', classes: 'bg-score-excellent/10 text-score-excellent' },
    running: { icon: Loader, label: 'En cours', classes: 'bg-orion-primary-soft text-[#818CF8]' },
    pending: { icon: Clock, label: 'En attente', classes: 'bg-[#F4F5FA] text-[#8B8FA7]' },
    failed: { icon: XCircle, label: 'Erreur', classes: 'bg-score-critical/10 text-score-critical' },
  }
  const c = configs[status]
  const Icon = c.icon
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.classes}`}>
      <Icon className={`w-3.5 h-3.5 ${status === 'running' ? 'animate-spin' : ''}`} strokeWidth={1.25} />
      {c.label}
    </span>
  )
}

const SECTIONS = [
  { key: 'technical', label: 'Technique', icon: Shield, scoreKey: 'scoreTech' as const, path: 'audit/errors' },
  { key: 'onpage', label: 'On-Page', icon: FileText, scoreKey: 'scoreOnpage' as const, path: 'audit/errors' },
  { key: 'performance', label: 'Performance', icon: BarChart2, scoreKey: 'scorePerf' as const, path: 'performance/vitals' },
  { key: 'serp', label: 'SERP', icon: SearchIcon, scoreKey: 'scoreSerp' as const, path: 'rankings' },
]

export default function ProjectOverview() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [launchLoading, setLaunchLoading] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)

  const { data: client, loading: clientLoading, error: clientError } = useApi<Client>(`/api/clients/${projectId}`)
  const { data: audits, loading: auditsLoading, refetch: refetchAudits } = useApi<Audit[]>(`/api/clients/${projectId}/audits`)

  if (clientError === 'Unauthorized' || clientError === '401') {
    navigate('/login'); return null
  }

  const auditList = Array.isArray(audits) ? audits : []
  const latestDone = auditList.find(a => a.status === 'completed') ?? null
  const runningAudit = auditList.find(a => a.status === 'running' || a.status === 'pending') ?? null
  const loading = clientLoading || auditsLoading

  const handleLaunchAudit = async () => {
    setLaunchLoading(true); setLaunchError(null)
    const { data, error: err } = await apiPost<{ id: number }>(`/api/clients/${projectId}/audits`, {})
    setLaunchLoading(false)
    if (err) { setLaunchError(err); return }
    refetchAudits()
  }

  return (
    <>
      <ContentHeader
        breadcrumb={[
          { label: 'Projets', to: '/projects' },
          { label: client?.name ?? '...' },
        ]}
        title="Apercu du projet"
        subtitle={client?.domain}
        actions={
          <button
            onClick={handleLaunchAudit}
            disabled={launchLoading || !!runningAudit}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {launchLoading ? <Loader className="w-4 h-4 animate-spin" strokeWidth={1.25} /> : <Play className="w-4 h-4" strokeWidth={1.25} />}
            {runningAudit ? 'Audit en cours...' : 'Lancer un audit'}
          </button>
        }
      />

      {launchError && (
        <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm mb-6">{launchError}</div>
      )}

      {runningAudit && (
        <div className="flex items-center justify-between gap-4 bg-orion-primary-soft border border-orion-primary/20 rounded-2xl px-5 py-4 mb-6 group cursor-pointer hover:bg-orion-primary/20 transition-colors">
          <div className="flex items-center gap-3">
            <Loader className="w-5 h-5 text-[#818CF8] animate-spin" strokeWidth={1.25} />
            <div>
              <p className="text-[#818CF8] font-medium text-sm">Audit en cours</p>
              <p className="text-[#555A73] text-xs">Lance le {formatDate(runningAudit.createdAt)}</p>
            </div>
          </div>
        </div>
      )}

      {!loading && latestDone && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="col-span-2 lg:col-span-1 bg-white border border-[#E2E4ED] rounded-xl p-5 flex flex-col items-center justify-center">
              <ScoreGauge score={latestDone.score ?? 0} label="Score global" size="lg" />
            </div>
            {SECTIONS.map(section => {
              // null = donnee absente (PageSpeed non lance, aucun mot-cle GSC) -> tiret, pas 0
              const score = latestDone[section.scoreKey]
              const hasScore = typeof score === 'number' && Number.isFinite(score)
              const Icon = section.icon
              return (
                <Link key={section.key} to={`/p/${projectId}/${section.path}`} className="bg-white border border-[#E2E4ED] rounded-xl p-5 hover:border-[#C8CBD8] transition-colors group">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className="w-4 h-4 text-[#555A73] group-hover:text-orion-primary transition-colors" strokeWidth={1.25} />
                    <span className="text-[13px] text-[#8B8FA7] font-medium">{section.label}</span>
                  </div>
                  <p className="text-2xl font-bold font-mono" style={{ color: hasScore ? scoreColor(score as number) : '#C8CBD8' }}>
                    {hasScore ? score : '—'}
                    <span className="text-sm text-[#555A73] font-normal">{hasScore ? '/100' : ' n/d'}</span>
                  </p>
                </Link>
              )
            })}
          </div>

          <div className="bg-white border border-[#E2E4ED] rounded-xl p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#1B2559] text-sm">Dernier audit</h3>
              <span className="text-xs text-[#555A73]">{formatDate(latestDone.createdAt)}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><span className="text-[#8B8FA7]">Pages crawlees</span><p className="font-mono font-bold text-[#1B2559] mt-1">{latestDone.pagesCount}</p></div>
              <div><span className="text-[#8B8FA7]">Statut</span><div className="mt-1">{statusBadge(latestDone.status)}</div></div>
            </div>
          </div>
        </>
      )}

      {!loading && !latestDone && !runningAudit && (
        <EmptyState
          icon={Zap}
          title="Aucun audit"
          description="Lancez votre premier audit pour analyser ce site."
          action={{ label: 'Lancer un audit', onClick: handleLaunchAudit }}
        />
      )}

      {!loading && auditList.length > 0 && (
        <div>
          <h3 className="font-semibold text-[#1B2559] text-sm mb-4">Historique des audits</h3>
          <div className="space-y-2">
            {auditList.map(audit => (
              <Link key={audit.id} to={`/p/${projectId}/audit`} className="block group flex items-center justify-between gap-4 bg-white border border-[#E2E4ED] rounded-xl px-5 py-3.5 hover:border-[#C8CBD8] hover:bg-[#F4F5FA] transition-all">
                <div className="flex items-center gap-4 min-w-0">
                  {statusBadge(audit.status)}
                  <span className="text-sm text-[#8B8FA7] truncate">{formatDate(audit.createdAt)}</span>
                  <span className="text-xs text-[#555A73] font-mono">{audit.pagesCount} pages</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {audit.score !== null ? (
                    <span className="font-bold font-mono text-base" style={{ color: scoreColor(audit.score) }}>
                      {audit.score}<span className="text-xs text-[#555A73] font-normal">/100</span>
                    </span>
                  ) : (
                    <span className="text-sm text-[#555A73]">-</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </>
  )
}
